import { useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import { Plane, Building2, PartyPopper, FileText, CheckCircle2, Loader2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ReservationForm = ({ variant = 'default' }) => {
  const { t } = useLanguage();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service_type: '',
    pickup_location: '',
    dropoff_location: '',
    pickup_date: '',
    pickup_time: '',
    passengers: 1,
    notes: '',
    requires_invoice: false,
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (checked) => {
    setFormData((prev) => ({ ...prev, requires_invoice: checked }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/reservations`, formData);
      setIsSuccess(true);
      toast.success(t('form.toast.success'), {
        description: t('form.toast.successDesc'),
      });
      setFormData({
        name: '',
        email: '',
        phone: '',
        service_type: '',
        pickup_location: '',
        dropoff_location: '',
        pickup_date: '',
        pickup_time: '',
        passengers: 1,
        notes: '',
        requires_invoice: false,
      });
      setTimeout(() => setIsSuccess(false), 3000);
    } catch (error) {
      toast.error(t('form.toast.error'), {
        description: t('form.toast.errorDesc'),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const serviceOptions = [
    { value: 'aeropuerto', label: t('form.serviceOptions.airport'), icon: Plane },
    { value: 'corporativo', label: t('form.serviceOptions.corporate'), icon: Building2 },
    { value: 'eventos', label: t('form.serviceOptions.events'), icon: PartyPopper },
  ];

  const isGlass = variant === 'glass';

  return (
    <form
      onSubmit={handleSubmit}
      data-testid="reservation-form"
      className={`space-y-6 ${
        isGlass
          ? 'glass rounded-2xl p-8 shadow-2xl'
          : 'bg-white rounded-2xl p-8 shadow-xl shadow-slate-200/50 border border-slate-100'
      }`}
    >
      {isSuccess ? (
        <div className="flex flex-col items-center justify-center py-12 animate-fade-in">
          <CheckCircle2 className="w-16 h-16 text-emerald-500 mb-4" />
          <h3 className="text-2xl font-bold text-slate-900 mb-2">{t('form.success.title')}</h3>
          <p className="text-slate-500 text-center">
            {t('form.success.message')}
          </p>
        </div>
      ) : (
        <>
          <div className="text-center mb-8">
            <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">
              {t('form.title')}
            </h3>
            <p className="text-slate-500">{t('form.subtitle')}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">{t('form.name')}</Label>
              <Input
                id="name"
                name="name"
                data-testid="input-name"
                placeholder={t('form.namePlaceholder')}
                value={formData.name}
                onChange={handleInputChange}
                required
                className="input-custom"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">{t('form.email')}</Label>
              <Input
                id="email"
                name="email"
                type="email"
                data-testid="input-email"
                placeholder={t('form.emailPlaceholder')}
                value={formData.email}
                onChange={handleInputChange}
                required
                className="input-custom"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="phone">{t('form.phone')}</Label>
              <Input
                id="phone"
                name="phone"
                data-testid="input-phone"
                placeholder={t('form.phonePlaceholder')}
                value={formData.phone}
                onChange={handleInputChange}
                required
                className="input-custom"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="service_type">{t('form.serviceType')}</Label>
              <Select
                value={formData.service_type}
                onValueChange={(value) => handleSelectChange('service_type', value)}
              >
                <SelectTrigger data-testid="select-service" className="input-custom">
                  <SelectValue placeholder={t('form.servicePlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  {serviceOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      <div className="flex items-center gap-2">
                        <option.icon className="w-4 h-4" />
                        {option.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="pickup_location">{t('form.pickup')}</Label>
              <Input
                id="pickup_location"
                name="pickup_location"
                data-testid="input-pickup"
                placeholder={t('form.pickupPlaceholder')}
                value={formData.pickup_location}
                onChange={handleInputChange}
                required
                className="input-custom"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="dropoff_location">{t('form.dropoff')}</Label>
              <Input
                id="dropoff_location"
                name="dropoff_location"
                data-testid="input-dropoff"
                placeholder={t('form.dropoffPlaceholder')}
                value={formData.dropoff_location}
                onChange={handleInputChange}
                required
                className="input-custom"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="pickup_date">{t('form.date')}</Label>
              <Input
                id="pickup_date"
                name="pickup_date"
                type="date"
                data-testid="input-date"
                value={formData.pickup_date}
                onChange={handleInputChange}
                required
                className="input-custom"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="pickup_time">{t('form.time')}</Label>
              <Input
                id="pickup_time"
                name="pickup_time"
                type="time"
                data-testid="input-time"
                value={formData.pickup_time}
                onChange={handleInputChange}
                required
                className="input-custom"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="passengers">{t('form.passengers')}</Label>
              <Select
                value={formData.passengers.toString()}
                onValueChange={(value) => handleSelectChange('passengers', parseInt(value))}
              >
                <SelectTrigger data-testid="select-passengers" className="input-custom">
                  <SelectValue placeholder="1" />
                </SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4, 5, 6, 7, 8].map((num) => (
                    <SelectItem key={num} value={num.toString()}>
                      {num} {num === 1 ? t('form.passengerSingular') : t('form.passengerPlural')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">{t('form.notes')}</Label>
            <Textarea
              id="notes"
              name="notes"
              data-testid="input-notes"
              placeholder={t('form.notesPlaceholder')}
              value={formData.notes}
              onChange={handleInputChange}
              className="input-custom min-h-[100px]"
            />
          </div>

          <div className="flex items-center space-x-3 p-4 bg-emerald-50 rounded-xl border border-emerald-200">
            <Checkbox
              id="requires_invoice"
              data-testid="checkbox-invoice"
              checked={formData.requires_invoice}
              onCheckedChange={handleCheckboxChange}
            />
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-emerald-600" />
              <Label
                htmlFor="requires_invoice"
                className="text-emerald-700 font-medium cursor-pointer"
              >
                {t('form.invoice')}
              </Label>
            </div>
          </div>

          <Button
            type="submit"
            data-testid="submit-reservation"
            disabled={isSubmitting}
            className="w-full bg-slate-900 text-white hover:bg-slate-800 rounded-full py-4 font-medium transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] shadow-lg"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                {t('form.submitting')}
              </>
            ) : (
              t('form.submit')
            )}
          </Button>
        </>
      )}
    </form>
  );
};
